import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { useVisibilityAwareInterval } from '@/common/hooks/use-visibility-aware-interval.ts'
import { useSelector } from 'react-redux'
import { selectIdeaListInput } from '@/redux/slices/idea-list-input.slice.ts'
import { useAppSelector } from '@/redux/hooks.ts'
import { selectUser } from '@/features/user/user.slice.ts'
import {
  getBaseQuery,
  getBaseUrl,
  REQUEST_TIMEOUT_MS,
} from '@/apis/api.util.ts'
import type { BaseQueryApi } from '@reduxjs/toolkit/query'
import {
  IdeaListItem,
  IdeaListRequest,
  IdeaListResponse,
} from '@/apis/idea/idea-api.def.ts'
import dayjs from 'dayjs'
import { IdeaCard } from './idea-card.tsx'
import { selectThemeStyle } from '@/features/setting/setting.slice.ts'
import { IdeyaSpinner } from '@/common/components/ideya-spinner.tsx'
import { FIRM_ROLE, GROUP_ROLE } from '@/router/roles.ts'
import { splitCategory } from '@/utils/selector-options.ts'

interface IdeaListMobileProps {
  onEditIdea: (id: string) => void
  onRefreshed?: () => void
}

function buildIdeaListUrl(request: IdeaListRequest) {
  const params = new URLSearchParams({
    page: String(request.page),
    pageSize: String(request.pageSize ?? ''),
    includeLiquidated: String(request.includeLiquidated),
    firmId: request.firmId ?? '',
    userId: request.userId ?? '',
    region: request.region ?? '',
    netCategory: request.netCategory ?? '',
    orderBy: request.orderBy ?? '',
    orderDirection: request.orderDirection ?? '',
    filterColumn: request.filterColumn ?? '',
    filterText: request.filterText ?? '',
    startDate: request.startDate ?? '',
    endDate: request.endDate ?? '',
  })
  return `/list?${params.toString()}`
}

const ideaBaseQuery = getBaseQuery(getBaseUrl('idea'), REQUEST_TIMEOUT_MS)

export interface IdeaListMobileRef {
  refresh: () => void
}

export const IdeaListMobile = forwardRef<
  IdeaListMobileRef,
  IdeaListMobileProps
>(({ onEditIdea, onRefreshed }, ref) => {
  const themeStyle = useAppSelector(selectThemeStyle)
  const ideaListInput = useSelector(selectIdeaListInput)
  const userInfo = useAppSelector(selectUser)
  const [ideas, setIdeas] = useState<IdeaListItem[]>([])
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const observerTarget = useRef<HTMLDivElement>(null)
  const pageSize = 20
  const pageRef = useRef(page)
  const isInternalUser = userInfo.role !== ''

  useEffect(() => {
    pageRef.current = page
  }, [page])

  const fetchIdeas = useCallback(
    async (pageNum: number, reset: boolean = false) => {
      if (!reset && (loading || !hasMore)) return

      setLoading(true)

      const { region, netCategory } = splitCategory(ideaListInput.category)

      const isManagerOrCompliance =
        userInfo.groupRole === GROUP_ROLE.ROLE_MANAGER ||
        userInfo.firmRole === FIRM_ROLE.ROLE_COMPLIANCE

      const userId =
        ideaListInput.onlyMyIdea && !isInternalUser && !isManagerOrCompliance
          ? userInfo.sub
          : ideaListInput.contributor || ''

      const rawStartDate = ideaListInput.dateRange[0]
      const rawEndDate = ideaListInput.dateRange[1]

      const startDate = dayjs(rawStartDate).isValid()
        ? dayjs(rawStartDate).format('YYYY-MM-DD')
        : undefined

      const endDate = dayjs(rawEndDate).isValid()
        ? dayjs(rawEndDate).format('YYYY-MM-DD')
        : undefined

      const [orderBy, orderDirection] = ideaListInput.mobileViewCardOrder
        ? ideaListInput.mobileViewCardOrder.split('/')
        : ['', '']

      const request: IdeaListRequest = {
        page: pageNum,
        pageSize: pageSize,
        includeLiquidated: ideaListInput.showLiquidatedIdeas,
        firmId: ideaListInput.firm || '',
        userId: userId,
        region,
        netCategory,
        orderBy: orderBy,
        orderDirection: orderDirection,
        startDate: startDate,
        endDate: endDate,
        filterColumn: '',
        filterText: '',
      }

      try {
        const result = await ideaBaseQuery(
          {
            url: buildIdeaListUrl(request),
            method: 'GET',
          },
          {} as BaseQueryApi,
          {}
        )

        const dataResult = result as { data?: IdeaListResponse }

        if (dataResult && dataResult.data && dataResult.data.data) {
          const newIdeas = dataResult.data.data.ideas
          setIdeas((prev) => (reset ? newIdeas : [...prev, ...newIdeas]))
          setHasMore(newIdeas.length === pageSize)
        }
        return true
      } catch (error) {
        console.error('Failed to fetch ideas:', error)
        return false
      } finally {
        setLoading(false)
      }
    },
    [
      loading,
      hasMore,
      ideaListInput.category,
      ideaListInput.onlyMyIdea,
      ideaListInput.dateRange,
      ideaListInput.firm,
      ideaListInput.contributor,
      ideaListInput.showLiquidatedIdeas,
      userInfo.sub,
      userInfo.groupRole,
      userInfo.firmRole,
      ideaListInput.mobileViewCardOrder,
      isInternalUser,
    ]
  )

  // Expose refresh method to parent
  useImperativeHandle(ref, () => ({
    refresh: async () => {
      const success = await fetchIdeas(1, true)
      if (success) {
        onRefreshed?.()
      }
    },
  }))

  const fetchIdeasRef = useRef(fetchIdeas)
  const onRefreshedRef = useRef(onRefreshed)

  useEffect(() => {
    fetchIdeasRef.current = fetchIdeas
  }, [fetchIdeas])

  useEffect(() => {
    onRefreshedRef.current = onRefreshed
  }, [onRefreshed])

  // Reset and fetch when filters change
  useEffect(() => {
    setPage(1)
    setHasMore(true)
    fetchIdeasRef.current(1, true)
  }, [
    ideaListInput.category,
    ideaListInput.firm,
    ideaListInput.contributor,
    ideaListInput.dateRange,
    ideaListInput.onlyMyIdea,
    ideaListInput.showLiquidatedIdeas,
    ideaListInput.mobileViewCardOrder,
  ])

  // Periodically refresh all loaded pages, skip when page is not visible.
  // The returned promise lets the hook skip ticks while a refresh is in flight.
  useVisibilityAwareInterval(
    async () => {
      const results = await Promise.all(
        Array.from({ length: pageRef.current }, (_, idx) =>
          fetchIdeasRef.current(idx + 1, idx === 0)
        )
      )
      if (results.every(Boolean)) {
        onRefreshedRef.current?.()
      }
    },
    Number(import.meta.env.VITE_GET_IDEA_LIST_INTERVAL)
  )

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          const nextPage = pageRef.current + 1
          setPage(nextPage)
          fetchIdeas(nextPage)
        }
      },
      { threshold: 0.1 }
    )

    if (observerTarget.current) {
      observer.observe(observerTarget.current)
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current)
      }
    }
  }, [hasMore, loading, fetchIdeas])

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: themeStyle.marginSM,
      }}
    >
      {ideas.map((idea) => (
        <IdeaCard key={idea.id} idea={idea} onEditIdea={onEditIdea} />
      ))}
      <div ref={observerTarget} style={{ height: '20px', margin: '20px 0' }}>
        {loading && (
          <div style={{ textAlign: 'center' }}>
            <IdeyaSpinner />
          </div>
        )}
      </div>

      {!hasMore && ideas.length > 0 && (
        <div
          style={{
            textAlign: 'center',
            color: '#999',
            padding: '20px',
          }}
        >
          No more ideas to load
        </div>
      )}

      {!loading && ideas.length === 0 && (
        <div
          style={{
            textAlign: 'center',
            color: '#999',
            padding: '40px',
          }}
        >
          No ideas found
        </div>
      )}
    </div>
  )
})
